import { DetectionType, QuickResolveActionItem } from '@hakimo-ui/hakimo/types';
import {
  incorrectDetectionOptions,
  personDetectedOptions,
  vehicleDetectedOptions,
} from '@hakimo-ui/hakimo/util';
import { useCallback, useState } from 'react';
import {
  getQuickResolveActionLabel,
  hasQuickResolveSubActions,
  isQuickResolveAction,
} from '../sop-manager/type-guards';
import { ResolveSectionSelection } from './ResolveSection';
import { ResolveSections } from './ResolveSections';
import { ResolveSubActions } from './ResolveSubActions';

// Filter options at module level since they're constants
const personOptions = personDetectedOptions.filter((opt) => opt !== 'Other');
const vehicleOptions = vehicleDetectedOptions.filter((opt) => opt !== 'Other');
const incorrectOptions = incorrectDetectionOptions.filter(
  (opt) => opt !== 'Other'
);

const EMPTY_SELECTION: ResolveSectionSelection = {
  chip: null,
  customInput: '',
};

type SectionSelections = Record<DetectionType, ResolveSectionSelection>;

const getInitialSelections = (): SectionSelections => ({
  [DetectionType.PERSON]: { ...EMPTY_SELECTION },
  [DetectionType.VEHICLE]: { ...EMPTY_SELECTION },
  [DetectionType.INCORRECT_DETECTION]: { ...EMPTY_SELECTION },
  [DetectionType.CORRECT_DETECTION]: { ...EMPTY_SELECTION },
  [DetectionType.INCORRECT_DETECTION_V2]: { ...EMPTY_SELECTION },
});

const hasSelection = (selection: ResolveSectionSelection): boolean =>
  selection.chip !== null || selection.customInput.trim() !== '';

const getCommentPrefix = (type: DetectionType) => {
  switch (type) {
    case DetectionType.PERSON:
      return 'Person Present';
    case DetectionType.VEHICLE:
      return 'Moving Vehicle';
    case DetectionType.INCORRECT_DETECTION:
      return 'False Positive';
    default:
      return '';
  }
};

interface Props {
  onClose: () => void;
  onSubmitResolveAction: (
    comment: string,
    action?: QuickResolveActionItem,
    subAction?: string
  ) => void;
  isLoading: boolean;
  actionWithSubactions?: QuickResolveActionItem;
}

export function ResolveAction(props: Props) {
  const { onClose, onSubmitResolveAction, isLoading, actionWithSubactions } =
    props;
  const [activeSection, setActiveSection] = useState<DetectionType | null>(
    null
  );
  const [selections, setSelections] =
    useState<SectionSelections>(getInitialSelections);

  // Determine which section currently has a selection
  const activeSectionWithSelection = (
    Object.entries(selections) as [DetectionType, ResolveSectionSelection][]
  ).find(([, sel]) => hasSelection(sel))?.[0];

  const handleSelectionChange = useCallback(
    (sectionType: DetectionType) => (newSelection: ResolveSectionSelection) => {
      setSelections(() => {
        // Clear all other sections when a new selection is made
        const updated = getInitialSelections();
        updated[sectionType] = newSelection;
        return updated;
      });
    },
    []
  );

  const hasSubActions =
    actionWithSubactions && hasQuickResolveSubActions(actionWithSubactions);
  const action =
    actionWithSubactions && isQuickResolveAction(actionWithSubactions)
      ? actionWithSubactions
      : undefined;
  const subActions = action?.subActions || [];

  const handleSubActionClick = (subAction: string) => {
    if (!actionWithSubactions) return;
    const label = getQuickResolveActionLabel(actionWithSubactions);
    const comment = `${label} - ${subAction}`;
    onSubmitResolveAction(comment, actionWithSubactions, subAction);
  };

  const handleConfirm = (detectionType: DetectionType, value: string) => {
    setActiveSection(detectionType);
    const prefix = getCommentPrefix(detectionType);
    const comment = `${prefix} - ${value}`;
    onSubmitResolveAction(comment);
  };

  if (hasSubActions && actionWithSubactions) {
    return (
      <ResolveSubActions
        onClose={onClose}
        actionWithSubactions={actionWithSubactions}
        subActions={subActions}
        onSubActionClick={handleSubActionClick}
        isLoading={isLoading}
      />
    );
  }

  return (
    <ResolveSections
      onClose={onClose}
      selections={selections}
      activeSection={activeSection}
      activeSectionWithSelection={activeSectionWithSelection}
      isLoading={isLoading}
      onConfirm={handleConfirm}
      onSelectionChange={handleSelectionChange}
      personOptions={personOptions}
      vehicleOptions={vehicleOptions}
      incorrectOptions={incorrectOptions}
    />
  );
}
